import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, Instagram, MessageCircle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { toast } from 'sonner';

export const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.message) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        setLoading(true);
        // Simulate form submission
        setTimeout(() => {
            toast.success('Message sent! We\'ll get back to you soon.');
            setFormData({ name: '', email: '', subject: '', message: '' });
            setLoading(false);
        }, 1000);
    };

    const contactInfo = [
        { icon: Mail, label: 'EMAIL', value: 'hello@yessuhchill.com', href: 'mailto:hello@yessuhchill.com' },
        { icon: Phone, label: 'PHONE', value: '+91 91375 77682', href: 'tel:+919137577682' },
        { icon: Clock, label: 'HOURS', value: 'Mon - Sat: 10AM - 8PM', href: '#' },
    ];

    return (
        <Layout>
            <div className="pt-32 md:pt-40 pb-16" data-testid="contact-page">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    {/* Header */}
                    <div className="text-center mb-12 md:mb-16">
                        <span className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">
                            GET IN TOUCH
                        </span>
                        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl mb-4">
                            CONTACT US
                        </h1>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            Got questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <h2 className="font-heading text-2xl mb-6">SEND A MESSAGE</h2>
                            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                            YOUR NAME *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                            placeholder="John Doe"
                                            data-testid="contact-name"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                            EMAIL ADDRESS *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                            placeholder="john@example.com"
                                            data-testid="contact-email"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                        SUBJECT
                                    </label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                        placeholder="How can we help?"
                                        data-testid="contact-subject"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                        MESSAGE *
                                    </label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows={6}
                                        className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime resize-none"
                                        placeholder="Tell us what's on your mind..."
                                        data-testid="contact-message"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-lime text-black py-4 text-sm font-bold tracking-[0.14em] uppercase flex items-center justify-center gap-2 hover:tracking-[0.2em] transition-all duration-300 disabled:opacity-50"
                                    data-testid="contact-submit"
                                >
                                    <Send className="w-4 h-4" />
                                    {loading ? 'SENDING...' : 'SEND MESSAGE'}
                                </button>
                            </form>
                        </motion.div>

                        {/* Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <h2 className="font-heading text-2xl mb-6">CONTACT INFO</h2>
                            <div className="space-y-6 mb-10">
                                {contactInfo.map((item) => (
                                    <a
                                        key={item.label}
                                        href={item.href}
                                        className="flex items-start gap-4 p-4 bg-card border border-border hover:border-lime/50 transition-colors group"
                                    >
                                        <div className="w-12 h-12 bg-lime/10 flex items-center justify-center flex-shrink-0 group-hover:bg-lime/20 transition-colors">
                                            <item.icon className="w-5 h-5 text-lime" />
                                        </div>
                                        <div>
                                            <span className="text-xs tracking-[0.15em] uppercase text-muted-foreground block mb-1">
                                                {item.label}
                                            </span>
                                            <span className="text-sm font-medium">{item.value}</span>
                                        </div>
                                    </a>
                                ))}
                            </div>

                            {/* Social Links */}
                            <h3 className="font-heading text-xl mb-4">FOLLOW US</h3>
                            <div className="flex gap-3">
                                <a
                                    href="https://instagram.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white text-xs font-bold tracking-wider uppercase hover:opacity-90 transition-opacity"
                                >
                                    <Instagram className="w-4 h-4" />
                                    INSTAGRAM
                                </a>
                                <a
                                    href="https://wa.me/919137577682"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-5 py-3 bg-green-600 text-white text-xs font-bold tracking-wider uppercase hover:bg-green-700 transition-colors"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    WHATSAPP
                                </a>
                            </div>

                            {/* FAQ Teaser */}
                            <div className="mt-10 p-6 bg-lime/10 border border-lime/30">
                                <h3 className="font-heading text-xl mb-2">NEED QUICK ANSWERS?</h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Check out our FAQ section for instant answers to common questions about orders, shipping, returns, and more.
                                </p>
                                <a href="#" className="text-lime text-sm font-bold tracking-wider uppercase hover:underline">
                                    VIEW FAQ →
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
